import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { Action } from '../../types/toolbar';
import { Toolbar } from '../../components/ui/toolbar/toolbar.component';
import CustomModal from '../../components/ui/modal/modal.component';
import StatusTransitionActionForm from '../../components/ui/statusTransitionActionForm/statusTransitionActionForm.component';
import { useCRUD } from '../../api/crud';
import { JOB_STATUS_TRANSITION_ACTIONS_ENDPOINT, SUCCESS_SNACKBAR, ERROR_SNACKBAR } from '../../consts';
import { useHasPermission } from '../../contexts/auth';
import { TENANT_ROUTES } from '../../workspaces/tenantConfig/tenantConfig.routes';
import { useSnackbar } from 'notistack';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';

export const TenantStatusTransitionActionActions = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { loading, error, remove } = useCRUD(JOB_STATUS_TRANSITION_ACTIONS_ENDPOINT);
  const { get } = useCRUD(JOB_STATUS_TRANSITION_ACTIONS_ENDPOINT);
  const [editOpened, setEditOpened] = useState<boolean>(false);
  const [deleteOpened, setDeleteOpened] = useState<boolean>(false);
  const [isProtected, setIsProtected] = useState<boolean>(false);
  const canChange = useHasPermission("service.change_jobstatustransitionaction");
  const canDelete = useHasPermission("service.delete_jobstatustransitionaction");

  useEffect(() => {
    const fetchAction = async () => {
      if (!id) {
        return;
      }

      const result = await get(id);
      if (result?.success && result.data) {
        setIsProtected(Boolean(result.data.is_protected));
      }
    };

    fetchAction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleDelete = async () => {
    if (loading || !id || isProtected) return;
    const result = await remove({ id });
    if (result?.success) {
      enqueueSnackbar(t('Status transition action deleted successfully'), SUCCESS_SNACKBAR);
      setDeleteOpened(false);
      window.dispatchEvent(new Event("statusTransitionActionsUpdated"));
      navigate(TENANT_ROUTES.jobStatusActions);
    } else {
      enqueueSnackbar(t('Status transition action deletion failed'), ERROR_SNACKBAR);
    }
  };

  const actions: Action[] = [
    {
      icon: <EditIcon />,
      label: t('Edit status transition action'),
      onClick: () => setEditOpened(true),
      disabled: !canChange,
    },
    {
      icon: <DeleteIcon />,
      label: t('Delete status transition action'),
      onClick: () => setDeleteOpened(true),
      disabled: !canDelete || isProtected,
      color: "var(--errorColor)",
    },
  ];

  const errorMessage =
    typeof error === 'string' ? error : error?.message ?? error?.detail ?? '';

  const deleteModalContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {t('Deleting this status transition action will permanently remove it from the system.')}
      </Typography>
      <Alert severity="warning">
        <Typography variant="body2">
          {t('This action cannot be undone. All associated data will be permanently deleted.')}
        </Typography>
      </Alert>
      {isProtected && (
        <Alert severity="info">
          <Typography variant="body2">
            {t('This status transition action is protected and cannot be deleted.')}
          </Typography>
        </Alert>
      )}
      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
      <Button variant="contained" color="error" onClick={handleDelete} disabled={loading || isProtected}>
        {loading ? t('Processing...') : t('Confirm deletion')}
      </Button>
    </Box>
  );

  return (
    <>
      <Toolbar actions={actions} />
      <CustomModal
        opened={editOpened}
        setOpened={setEditOpened}
        title={t('Edit status transition action')}
        content={
          <StatusTransitionActionForm
            id={id}
            onSuccess={() => {
              setEditOpened(false);
              window.dispatchEvent(new Event("statusTransitionActionsUpdated"));
            }}
          />
        }
      />
      <CustomModal
        opened={deleteOpened}
        setOpened={setDeleteOpened}
        title={t('Delete status transition action')}
        content={deleteModalContent}
      />
    </>
  );
};
